import React, { useMemo } from 'react'

import { BarChart3, TrendingUp, Users, Target } from 'lucide-react'
import { useCRMStore } from '../store/crmStore'

export default function SalesReportsAnalyticsPage() {
  const customers = useCRMStore((state) => state.customers)
  const leads = useCRMStore((state) => state.leads)
  const opportunities = useCRMStore((state) => state.opportunities)
  const activities = useCRMStore((state) => state.activities)
  const contacts = useCRMStore((state) => state.contacts)

  const analytics = useMemo(() => {
    const stats = {
      totalCustomers: customers.length,
      activeCustomers: customers.filter(c => c.status === 'Active').length,
      prospectCustomers: customers.filter(c => c.status === 'Prospect').length,

      totalLeads: leads.length,
      newLeads: leads.filter(l => l.status === 'New').length,
      qualifiedLeads: leads.filter(l => l.status === 'Qualified').length,
      conversionRate: customers.length > 0 && (leads.length + customers.length) > 0
        ? Math.round((customers.length / (customers.length + leads.length)) * 100)
        : 0,

      totalOpportunities: opportunities.length,
      closedWon: opportunities.filter(o => o.stage === 'Closed Won').length,
      closedLost: opportunities.filter(o => o.stage === 'Closed Lost').length,
      winRate: opportunities.length > 0
        ? Math.round((opportunities.filter(o => o.stage === 'Closed Won').length / opportunities.filter(o => o.stage === 'Closed Won' || o.stage === 'Closed Lost').length) * 100) || 0
        : 0,

      totalRevenue: opportunities.filter(o => o.stage === 'Closed Won').reduce((sum, o) => sum + o.value, 0),
      totalPipeline: opportunities.filter(o => o.stage !== 'Closed Won' && o.stage !== 'Closed Lost').reduce((sum, o) => sum + o.value, 0),
      avgDealSize: opportunities.length > 0 ? opportunities.reduce((sum, o) => sum + o.value, 0) / opportunities.length : 0,

      totalActivities: activities.length,
      completedActivities: activities.filter(a => a.status === 'Completed').length,
      scheduledActivities: activities.filter(a => a.status === 'Scheduled').length,

      totalContacts: contacts.length,
      avgContactsPerCustomer: customers.length > 0 ? (contacts.length / customers.length).toFixed(1) : 0,
    }

    return stats
  }, [customers, leads, opportunities, activities, contacts])

  const topCustomers = useMemo(() => {
    return customers
      .map(c => ({
        ...c,
        revenue: opportunities.filter(o => o.customerId === c.id && o.stage === 'Closed Won').reduce((sum, o) => sum + o.value, 0),
        pipeline: opportunities.filter(o => o.customerId === c.id && o.stage !== 'Closed Won' && o.stage !== 'Closed Lost').reduce((sum, o) => sum + o.value, 0),
      }))
      .sort((a, b) => (b.revenue + b.pipeline) - (a.revenue + a.pipeline))
      .slice(0, 5)
  }, [customers, opportunities])

  const industryBreakdown = useMemo(() => {
    const industries = {}
    customers.forEach(c => {
      if (!industries[c.industry]) {
        industries[c.industry] = { count: 0, value: 0 }
      }
      industries[c.industry].count += 1
      industries[c.industry].value += c.value
    })

    return Object.entries(industries)
      .map(([industry, data]) => ({
        industry,
        ...data,
      }))
      .sort((a, b) => b.value - a.value)
  }, [customers])

  const sourcePerformance = useMemo(() => {
    const sources = {}
    leads.forEach(l => {
      if (!sources[l.source]) {
        sources[l.source] = { count: 0, qualified: 0, avgScore: 0, totalScore: 0 }
      }
      sources[l.source].count += 1
      sources[l.source].totalScore += l.score
      if (l.status === 'Qualified') {
        sources[l.source].qualified += 1
      }
    })

    return Object.entries(sources)
      .map(([source, data]) => ({
        source,
        count: data.count,
        qualified: data.qualified,
        qualificationRate: data.count > 0 ? Math.round((data.qualified / data.count) * 100) : 0,
        avgScore: data.count > 0 ? Math.round(data.totalScore / data.count) : 0,
      }))
      .sort((a, b) => b.count - a.count)
  }, [leads])

  return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-white mb-2">Sales Reports & Analytics</h1>
            <p className="text-slate-400">Comprehensive sales performance metrics and insights</p>
          </div>

          {/* Core Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-slate-400 text-sm mb-2">Win Rate</p>
                  <p className="text-3xl font-bold text-green-400">{analytics.winRate}%</p>
                </div>
                <TrendingUp className="text-green-500" size={24} />
              </div>
              <p className="text-xs text-slate-500">{analytics.closedWon} closed won</p>
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-slate-400 text-sm mb-2">Conversion Rate</p>
                  <p className="text-3xl font-bold text-blue-400">{analytics.conversionRate}%</p>
                </div>
                <Users className="text-blue-500" size={24} />
              </div>
              <p className="text-xs text-slate-500">Leads to customers</p>
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-slate-400 text-sm mb-2">Total Revenue</p>
                  <p className="text-3xl font-bold text-yellow-400">${(analytics.totalRevenue / 1000).toFixed(0)}K</p>
                </div>
                <BarChart3 className="text-yellow-500" size={24} />
              </div>
              <p className="text-xs text-slate-500">Closed deals</p>
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-slate-400 text-sm mb-2">Active Pipeline</p>
                  <p className="text-3xl font-bold text-purple-400">${(analytics.totalPipeline / 1000).toFixed(0)}K</p>
                </div>
                <Target className="text-purple-500" size={24} />
              </div>
              <p className="text-xs text-slate-500">{analytics.totalOpportunities - analytics.closedWon - analytics.closedLost} opportunities</p>
            </div>
          </div>

          {/* Summary Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Customer Metrics */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <h3 className="text-lg font-bold text-white mb-6">Customer Metrics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-blue-500/10">
                  <span className="text-slate-400">Total Customers</span>
                  <span className="text-2xl font-bold text-blue-300">{analytics.totalCustomers}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-blue-500/10">
                  <span className="text-slate-400">Active</span>
                  <span className="text-2xl font-bold text-green-400">{analytics.activeCustomers}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-blue-500/10">
                  <span className="text-slate-400">Prospects</span>
                  <span className="text-2xl font-bold text-yellow-400">{analytics.prospectCustomers}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Avg Contacts</span>
                  <span className="text-2xl font-bold text-purple-400">{analytics.avgContactsPerCustomer}</span>
                </div>
              </div>
            </div>

            {/* Lead Metrics */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <h3 className="text-lg font-bold text-white mb-6">Lead Metrics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-blue-500/10">
                  <span className="text-slate-400">Total Leads</span>
                  <span className="text-2xl font-bold text-blue-300">{analytics.totalLeads}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-blue-500/10">
                  <span className="text-slate-400">New</span>
                  <span className="text-2xl font-bold text-cyan-400">{analytics.newLeads}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-blue-500/10">
                  <span className="text-slate-400">Qualified</span>
                  <span className="text-2xl font-bold text-green-400">{analytics.qualifiedLeads}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Conversion Rate</span>
                  <span className="text-2xl font-bold text-yellow-400">{analytics.conversionRate}%</span>
                </div>
              </div>
            </div>

            {/* Opportunity Metrics */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <h3 className="text-lg font-bold text-white mb-6">Opportunity Metrics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-blue-500/10">
                  <span className="text-slate-400">Total Opportunities</span>
                  <span className="text-2xl font-bold text-blue-300">{analytics.totalOpportunities}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-blue-500/10">
                  <span className="text-slate-400">Closed Won</span>
                  <span className="text-2xl font-bold text-green-400">{analytics.closedWon}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-blue-500/10">
                  <span className="text-slate-400">Closed Lost</span>
                  <span className="text-2xl font-bold text-red-400">{analytics.closedLost}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Avg Deal Size</span>
                  <span className="text-2xl font-bold text-purple-400">${(analytics.avgDealSize / 1000).toFixed(0)}K</span>
                </div>
              </div>
            </div>
          </div>

          {/* Top Customers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 overflow-hidden">
              <div className="p-6 border-b border-blue-500/20">
                <h3 className="text-xl font-bold text-white">Top 5 Customers</h3>
              </div>

              <div className="divide-y divide-blue-500/10">
                {topCustomers.map((customer, index) => (
                  <div key={customer.id} className="p-6 hover:bg-slate-800/50 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="text-sm text-slate-400 mb-1">#{index + 1}</div>
                        <h4 className="font-semibold text-white">{customer.name}</h4>
                        <p className="text-xs text-slate-500">{customer.company}</p>
                      </div>
                      <span className="text-lg font-bold text-blue-400">${((customer.revenue + customer.pipeline) / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="flex gap-4 text-xs">
                      <span className="text-green-400">Revenue: ${(customer.revenue / 1000).toFixed(0)}K</span>
                      <span className="text-yellow-400">Pipeline: ${(customer.pipeline / 1000).toFixed(0)}K</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Industry Breakdown */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 overflow-hidden">
              <div className="p-6 border-b border-blue-500/20">
                <h3 className="text-xl font-bold text-white">Industry Breakdown</h3>
              </div>

              <div className="divide-y divide-blue-500/10">
                {industryBreakdown.map((industry) => (
                  <div key={industry.industry} className="p-6 hover:bg-slate-800/50 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-semibold text-white">{industry.industry}</h4>
                        <p className="text-xs text-slate-500">{industry.count} customers</p>
                      </div>
                      <span className="text-lg font-bold text-green-400">${(industry.value / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-green-500 to-cyan-500"
                        style={{ width: `${(industry.value / (industryBreakdown[0]?.value || 1)) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Lead Source Performance */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 overflow-hidden">
            <div className="p-6 border-b border-blue-500/20">
              <h3 className="text-xl font-bold text-white">Lead Source Performance</h3>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-blue-500/20 bg-slate-900/50">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Source</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Leads</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Qualified</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Qualification Rate</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Avg Score</th>
                  </tr>
                </thead>
                <tbody>
                  {sourcePerformance.map((source) => (
                    <tr key={source.source} className="border-b border-blue-500/10 hover:bg-slate-800/50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-white">{source.source}</td>
                      <td className="px-6 py-4 text-blue-300">{source.count}</td>
                      <td className="px-6 py-4 text-green-300">{source.qualified}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-yellow-500"
                              style={{ width: `${source.qualificationRate}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-yellow-300 font-semibold">{source.qualificationRate}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-purple-300 font-semibold">{source.avgScore}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
  )
}
